import { createAsyncActions, Epoch } from "@ctra/utils";
import { normalize } from "normalizr";

import {
  GamePlanEntity,
  GamePlanRequestPayload,
  TaskListRequestPayload,
  TodoItemEntity,
  TodoUpdateAction
} from "./typings";

import types from "./types";
import schemas, { NormalizedGamePlan, NormalizedTodoItemList } from "./schemas";
import { FarmEntity } from "../farms";
import { advanceTaskStatus } from "./utils";

/**
 * Fetch game plans for the given farm
 * @category Action
 */
const fetchGamePlans = createAsyncActions(
  types.FETCH_GAME_PLAN_LIST,
  (farmID: FarmEntity["id"]) => ({ farmID }),
  (farmID: FarmEntity["id"], gamePlanResponse: Array<Record<string, unknown>>) => ({
    farmID,
    ...normalize(gamePlanResponse, schemas.gamePlanList)
  }),
  (farmID: FarmEntity["id"], error: string) => ({ farmID, error })
);

/**
 * Create a game plan
 * @category Action
 */
const createGamePlan = createAsyncActions(
  types.CREATE_GAME_PLAN,
  (processedFormValues: GamePlanRequestPayload) => processedFormValues,
  (gamePlanResponse: Record<string, unknown>) => normalize(gamePlanResponse, schemas.gamePlan),
  (error: string) => ({ error })
);

/**
 * Update an existing game plan
 * @category Action
 */
const updateGamePlan = createAsyncActions(
  types.UPDATE_GAME_PLAN,
  (
    processedFormValues: GamePlanRequestPayload
  ): GamePlanRequestPayload & {
    id: GamePlanRequestPayload["id"];
  } => ({
    /**
     * This is the primary key so it needs to be included
     */
    id: processedFormValues.id,
    ...processedFormValues
  }),
  (
    gamePlanResponse: Record<string, unknown>
  ): NormalizedGamePlan & {
    id: GamePlanEntity["id"];
  } => ({
    /**
     * This is the primary key so it needs to be included
     */
    id: gamePlanResponse.id as GamePlanEntity["id"],
    ...normalize(gamePlanResponse, schemas.gamePlan)
  }),
  (id: GamePlanEntity["id"], error: string) => ({ id, error })
);

/**
 * Create a game plan
 * @category Action
 */
const deleteGamePlan = createAsyncActions(
  types.DELETE_GAME_PLAN,
  (id: GamePlanEntity["id"]) => ({ id }),
  (id: GamePlanEntity["id"]) => ({ id }),
  (id, error: string) => ({ id, error })
);

export type CreateTaskListFulfilledPayload = {
  gamePlanID: GamePlanEntity["id"];
} & NormalizedTodoItemList;

/**
 * Create a task list in the given game plan
 * @category Action
 */
const createTaskList = createAsyncActions(
  types.CREATE_TODO_LIST,
  (gamePlanID: GamePlanEntity["id"], processedFormValues: Array<TaskListRequestPayload>) => ({
    gamePlanID,
    taskList: processedFormValues
  }),
  (
    gamePlanID: GamePlanEntity["id"],
    taskListResponse: Array<Record<string, unknown>>
  ): CreateTaskListFulfilledPayload => ({
    gamePlanID,
    ...normalize(taskListResponse, schemas.todoItemList)
  }),
  (gamePlanID: GamePlanEntity["id"], error: string) => ({ gamePlanID, error })
);

/**
 * Replace a to-do list of the given game plan
 * @category Action
 */
const updateTaskList = createAsyncActions(
  types.UPDATE_TODO_LIST,
  (gamePlanID: GamePlanEntity["id"], processedFormValues: TaskListRequestPayload[]) => ({
    gamePlanID,
    taskList: processedFormValues
  }),
  (
    gamePlanID: GamePlanEntity["id"],
    taskListResponse: Array<Record<string, unknown>>
  ): CreateTaskListFulfilledPayload => ({
    gamePlanID,
    ...normalize(taskListResponse, schemas.todoItemList)
  }),
  (gamePlanID: GamePlanEntity["id"], error: string) => ({ gamePlanID, error })
);

/**
 * Update a single task
 * @category Action
 */
const updateTask = createAsyncActions(
  types.UPDATE_TASK,
  (
    gamePlanID: GamePlanEntity["id"],
    { status, modifiedEpoch, meta, ...task }: TodoItemEntity,
    action: TodoUpdateAction
  ): TodoItemEntity & {
    gamePlanID: GamePlanEntity["id"];
  } => ({
    gamePlanID,
    ...task,
    modifiedEpoch: action === TodoUpdateAction.advance ? Epoch.now() : modifiedEpoch,
    status: action === TodoUpdateAction.advance ? advanceTaskStatus(status) : status
  }),
  (task: TodoItemEntity): TodoItemEntity => task,
  (
    id,
    error: string
  ): {
    id: TodoItemEntity["id"];
    error: string;
  } => ({
    id,
    error
  })
);

export default {
  fetchGamePlans,
  createGamePlan,
  updateGamePlan,
  deleteGamePlan,
  createTaskList,
  updateTaskList,
  updateTask
};
